/**
 * Asset custom fields (AST-12) - the Asset-specific contract on top of the generic custom-field
 * infrastructure in `../custom-fields/` (see that module's header for the shared-infra decision).
 *
 * Admins define fields per asset CATEGORY (text, number, date, single choice; required flag;
 * ordering; active state; options). Values are stored per asset and travel as a map
 * `{ [fieldId]: string }` on the asset DTO / create / patch bodies: numbers are decimal strings and
 * dates are `YYYY-MM-DD`, never JS floats. The server validates every value against the category's
 * ACTIVE definitions (never trusts the client) and the inventory list can search/filter on them.
 *
 * Definition reads are open to anyone who can work with assets (the form needs them); writes need
 * `asset.category:manage`, the same key as editing the category itself.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { GENERIC_FIELD_TYPES } from '../custom-fields/types.js';
import {
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const assetCustomFieldTypeSchema = z.enum(GENERIC_FIELD_TYPES);

export const assetCustomFieldOptionSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, numbers, dashes or underscores'),
  label: z.string().trim().min(1).max(100),
  sortOrder: z.number().int().min(0).max(1000),
});
export type AssetCustomFieldOption = z.infer<typeof assetCustomFieldOptionSchema>;

/** One stored/submitted value per field id; `null` clears a value. */
export const assetCustomFieldValuesSchema = z.record(uuidSchema, z.string().max(2000).nullable());
export type AssetCustomFieldValues = z.infer<typeof assetCustomFieldValuesSchema>;

export const assetCustomFieldSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  categoryId: uuidSchema,
  type: assetCustomFieldTypeSchema,
  label: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  sortOrder: z.number().int(),
  /** `single_choice` only. */
  options: z.array(assetCustomFieldOptionSchema).nullable(),
  /** `text` only. */
  maxLength: z.number().int().nullable(),
  isActive: z.boolean(),
  /** Bumped when an edit could reinterpret stored values (options removed, maxLength lowered). */
  version: z.number().int(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AssetCustomField = z.infer<typeof assetCustomFieldSchema>;

export const createAssetCustomFieldBodySchema = z.object({
  type: assetCustomFieldTypeSchema,
  label: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).nullable().optional(),
  required: z.boolean().optional().default(false),
  /** Omitted = appended after the category's existing fields. */
  sortOrder: z.number().int().min(0).max(1000).optional(),
  options: z.array(assetCustomFieldOptionSchema).max(50).optional(),
  maxLength: z.number().int().min(1).max(2000).optional(),
});
export type CreateAssetCustomFieldBody = z.infer<typeof createAssetCustomFieldBodySchema>;

/** `type` is immutable: changing it would reinterpret every stored value. Deactivate and re-add. */
export const updateAssetCustomFieldBodySchema = z
  .object({
    label: z.string().trim().min(1).max(100).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    required: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
    options: z.array(assetCustomFieldOptionSchema).max(50).optional(),
    maxLength: z.number().int().min(1).max(2000).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type UpdateAssetCustomFieldBody = z.infer<typeof updateAssetCustomFieldBodySchema>;

export const listAssetCustomFieldsQuerySchema = z.object({
  /** Admin editor passes true so deactivated fields can be re-enabled; forms omit it. */
  includeInactive: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});

export const assetCustomFieldListSchema = z.object({ items: z.array(assetCustomFieldSchema) });
export type AssetCustomFieldList = z.infer<typeof assetCustomFieldListSchema>;

export const assetCustomFieldsContract = c.router(
  {
    listCategoryCustomFields: {
      method: 'GET',
      path: '/categories/:categoryId/custom-fields',
      pathParams: z.object({ categoryId: uuidSchema }),
      query: listAssetCustomFieldsQuerySchema,
      responses: {
        200: assetCustomFieldListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List the custom fields defined for an asset category',
      metadata: routeMeta([
        'asset.category:read',
        'asset:read',
        'asset:create',
        'asset:edit',
      ]),
    },
    createCategoryCustomField: {
      method: 'POST',
      path: '/categories/:categoryId/custom-fields',
      pathParams: z.object({ categoryId: uuidSchema }),
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: createAssetCustomFieldBodySchema,
      responses: {
        201: assetCustomFieldSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Add a custom field to an asset category',
      metadata: routeMeta('asset.category:manage'),
    },
    updateCategoryCustomField: {
      method: 'PATCH',
      path: '/categories/:categoryId/custom-fields/:fieldId',
      pathParams: z.object({ categoryId: uuidSchema, fieldId: uuidSchema }),
      body: updateAssetCustomFieldBodySchema,
      responses: {
        200: assetCustomFieldSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit, reorder or deactivate a category custom field',
      metadata: routeMeta('asset.category:manage'),
    },
  },
  { pathPrefix: '/asset-management' },
);
